import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/molecules/searchable-select";
import type { SearchableSelectItem } from "@/components/molecules/searchable-select";
import { useForms } from "@/features/qualifications/hooks/use-qualification-forms";
import { useTranslation } from "@/features/utils/i18n";
import { ArrowUpFromLine } from "lucide-react";
import type { CustomFieldProps } from "./dynamic-form";

/**
 * Custom field for selecting qualification forms (multiple selection)
 */
export function QualificationFormField({
  fieldName,
  fieldSchema,
  value,
  onChange,
  isRequired,
  inheritable = false,
  parentValue,
  onToggleInherit,
}: CustomFieldProps) {
  const { t } = useTranslation("tools");
  const { data: forms = [], isLoading } = useForms({ status: "active" });

  const label = fieldSchema.title || fieldName;
  const description = fieldSchema.description;
  const isInheriting = inheritable && value === undefined;

  // Convert forms to SearchableSelectItem format
  const items: SearchableSelectItem[] = forms.map((form) => ({
    id: form.id,
    label: form.name,
    description: form.description,
  }));

  // Ensure value is an array
  const currentValue = Array.isArray(value) ? value : value ? [value] : [];

  // Format parent value for display
  const formatParentValue = () => {
    if (!parentValue) return t("tools.form.notSet");
    if (Array.isArray(parentValue)) {
      if (parentValue.length === 0)
        return t("tools.qualificationForms.allActive");
      return t("tools.qualificationForms.count", { count: parentValue.length });
    }
    return t("tools.form.complexValue");
  };

  // Render inherit/override toggle
  const renderInheritToggle = () => {
    if (!inheritable || !onToggleInherit) return null;

    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onToggleInherit}
        className="h-6 px-2 text-xs"
      >
        {isInheriting ? (
          <>
            <ArrowUpFromLine className="w-3 h-3 mr-1" />
            {t("tools.form.override")}
          </>
        ) : (
          <>
            <ArrowUpFromLine className="w-3 h-3 mr-1" />
            {t("tools.form.inherit")}
          </>
        )}
      </Button>
    );
  };

  // If inheriting, show collapsed view
  if (isInheriting) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-muted-foreground">
            {label}
            {isRequired && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderInheritToggle()}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/50 rounded-md border border-dashed">
          <ArrowUpFromLine className="w-4 h-4" />
          <span>
            {t("tools.form.inheriting")} <strong>{formatParentValue()}</strong>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={fieldName}>
          {label}
          {isRequired && <span className="text-destructive ml-1">*</span>}
        </Label>
        {renderInheritToggle()}
      </div>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      <SearchableSelect
        items={items}
        value={currentValue}
        onChange={onChange}
        multiple={true}
        placeholder={t("tools.qualificationForms.placeholder")}
        searchPlaceholder={t("tools.qualificationForms.search")}
        emptyText={t("tools.qualificationForms.empty")}
        isLoading={isLoading}
        allowNone={true}
        noneOption={{
          id: "",
          label: t("tools.qualificationForms.noneLabel"),
          description: t("tools.qualificationForms.noneDescription"),
        }}
        showBadges={true}
        showSelectAll={true}
      />
    </div>
  );
}
